import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { CorporateOperation, CorporateOperationCreateDTO, CorporateOperationFile, CorporateOperationUpdateDTO } from '../../models/corporate-operation.model';

@Injectable({
  providedIn: 'root',
})
export class CorporateOperationsService {
  private readonly base = `${environment.apiUrl}/client-corporate-operations`;

  constructor(private http: HttpClient) {}

  async create(dto: CorporateOperationCreateDTO): Promise<CorporateOperation> {
    return lastValueFrom(this.http.post<CorporateOperation>(this.base, dto));
  }

  async getByClient(clientId: number): Promise<CorporateOperation[]> {
    return lastValueFrom(this.http.get<CorporateOperation[]>(`${this.base}/${clientId}`));
  }

  async getOne(id: number): Promise<CorporateOperation> {
    return lastValueFrom(this.http.get<CorporateOperation>(`${this.base}/detail/${id}`));
  }

  async update(id: number, dto: CorporateOperationUpdateDTO): Promise<CorporateOperation> {
    return lastValueFrom(this.http.put<CorporateOperation>(`${this.base}/${id}`, dto));
  }

  async delete(id: number): Promise<void> {
    await lastValueFrom(this.http.delete(`${this.base}/${id}`));
  }

  // ── Document management ────────────────────────────────────────────────────

  async uploadDocument(operationId: number, file: File): Promise<CorporateOperationFile> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    return lastValueFrom(
      this.http.post<CorporateOperationFile>(`${this.base}/${operationId}/documents`, formData)
    );
  }

  async getDocuments(operationId: number): Promise<CorporateOperationFile[]> {
    return lastValueFrom(
      this.http.get<CorporateOperationFile[]>(`${this.base}/${operationId}/documents`)
    );
  }

  async downloadDocument(operationId: number, fileId: string): Promise<Blob> {
    return lastValueFrom(
      this.http.get(`${this.base}/${operationId}/documents/${fileId}/download`, { responseType: 'blob' })
    );
  }

  async deleteDocument(operationId: number, fileId: string): Promise<void> {
    await lastValueFrom(this.http.delete(`${this.base}/${operationId}/documents/${fileId}`));
  }
}
